import { normalizeSectorName } from "@/utils/sectorUtils";
import type { EntityData } from "@/types/entity";
import type { DecizieInregistrare } from "@/types/decizieInregistrare";
import type { Sector } from "@/types/risk-analysis";

export type SectorMismatchResult = {
  hasMismatch: boolean;
  onlyInNotificare: string[];
  onlyInDecizie: string[];
};

export type SeedingSectorsResult = {
  sectorIds: string[];
  sectorNames: string[];
  source: "decizie" | "notificare";
  mismatch: SectorMismatchResult | null;
};

export function getNotificareSectorNames(
  entityData: EntityData | null
): string[] {
  if (!entityData?.selected_services?.length) return [];
  return [...new Set(entityData.selected_services.map((s) => s.sector_name))];
}

export function getDecizieSectorNames(
  decizie: DecizieInregistrare | null
): string[] {
  return decizie?.sectors ?? [];
}

export function detectSectorMismatch(
  notificareSectors: string[],
  decizieSectors: string[]
): SectorMismatchResult {
  if (notificareSectors.length === 0 || decizieSectors.length === 0) {
    return { hasMismatch: false, onlyInNotificare: [], onlyInDecizie: [] };
  }

  const normalizedNotificare = notificareSectors.map(normalizeSectorName);
  const normalizedDecizie = decizieSectors.map(normalizeSectorName);

  const onlyInNotificare = notificareSectors.filter(
    (name) => !normalizedDecizie.includes(normalizeSectorName(name))
  );

  const onlyInDecizie = decizieSectors.filter(
    (name) => !normalizedNotificare.includes(normalizeSectorName(name))
  );

  return {
    hasMismatch: onlyInNotificare.length > 0 || onlyInDecizie.length > 0,
    onlyInNotificare,
    onlyInDecizie,
  };
}

export function resolveSeedingSectors(
  notificareSectors: string[],
  decizieSectors: string[],
  allSectors: Sector[]
): SeedingSectorsResult {
  const hasDecizie = decizieSectors.length > 0;
  const hasNotificare = notificareSectors.length > 0;

  const mismatch =
    hasDecizie && hasNotificare
      ? detectSectorMismatch(notificareSectors, decizieSectors)
      : null;

  const sourceSectors = hasDecizie ? decizieSectors : notificareSectors;
  const source: "decizie" | "notificare" = hasDecizie
    ? "decizie"
    : "notificare";

  const sectorIds = allSectors
    .filter((s) =>
      sourceSectors.some(
        (name) => normalizeSectorName(name) === normalizeSectorName(s.nume)
      )
    )
    .map((s) => s.id);

  const sectorNames = allSectors
    .filter((s) => sectorIds.includes(s.id))
    .map((s) => s.nume);

  return { sectorIds, sectorNames, source, mismatch };
}
